package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.toUpperCase
import androidx.compose.ui.unit.dp
import androidx.compose.ui.graphics.ColorFilter
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.getPlatform
import com.shadowings.gero.presentation.component.FeatureUnavailableDialog
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.component.StatType
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.ui.modalScrim
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.rememberShareManager
import compose.icons.FeatherIcons
import compose.icons.feathericons.RefreshCw
import gero.composeapp.generated.resources.F34
import gero.composeapp.generated.resources.N14
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.U28
import gero.composeapp.generated.resources.Y1
import gero.composeapp.generated.resources.cta_complete
import gero.composeapp.generated.resources.cta_complete_btn
import gero.composeapp.generated.resources.ho_capito_btn
import gero.composeapp.generated.resources.share_btn_text
import gero.composeapp.generated.resources.share_streak_many
import gero.composeapp.generated.resources.share_streak_one
import gero.composeapp.generated.resources.share_title
import gero.composeapp.generated.resources.streak_restoration_banner_hint
import gero.composeapp.generated.resources.streak_restoration_banner_many
import gero.composeapp.generated.resources.streak_restoration_banner_one
import gero.composeapp.generated.resources.stat_desc_coin
import gero.composeapp.generated.resources.stat_desc_heart
import gero.composeapp.generated.resources.stat_desc_star
import gero.composeapp.generated.resources.stat_desc_strike
import gero.composeapp.generated.resources.stat_title_coin
import gero.composeapp.generated.resources.stat_title_heart
import gero.composeapp.generated.resources.stat_title_star
import gero.composeapp.generated.resources.stat_title_strike
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource


@Composable
fun StatExplanationBottomSheet(
    statType: StatType,
    userData: UserData,
    onDismiss: () -> Unit,
    isFullVersionUnlocked: Boolean = false,
    onPurchaseFullVersion: () -> Unit = {},
    onRedeemStreakRestoration: (String) -> Unit = {}
) {
    val deviceInfo = rememberDeviceInfo()
    var showFeatureUnavailableDialog by remember { mutableStateOf(false) }
    val isWeb = getPlatform().isWeb
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(modalScrim)
            .clickable(
                indication = null,
                interactionSource = remember { MutableInteractionSource() }
            ) { onDismiss() }
            .semantics { testTag = "stat_explanation_bottom_sheet" }
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .background(
                    color = MaterialTheme.colorScheme.surface,
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(
                        24.dp,
                        24.dp,
                        0.dp,
                        0.dp
                    )
                )
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() }
                ) { /* block click to prevent dismiss */ }
                .align(Alignment.BottomCenter)
                .padding(24.dp)
                .semantics { testTag = "stat_explanation_content" },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Drag handle visual indicator
            Box(
                Modifier
                    .size(width = 40.dp, height = 4.dp)
                    .background(
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        shape = androidx.compose.foundation.shape.CircleShape
                    )
            )
            Spacer(Modifier.height(24.dp))

            // Icon
            val imageRes = when (statType) {
                StatType.STRIKE -> Res.drawable.U28
                StatType.STAR -> Res.drawable.N14
                StatType.LEVEL -> Res.drawable.Y1
                StatType.HEART -> Res.drawable.F34
            }
            Image(
                painter = painterResource(imageRes),
                contentDescription = null,
                modifier = Modifier.size(64.dp),
                colorFilter = ColorFilter.tint(hieroglyphTint)
            )
            Spacer(Modifier.height(16.dp))

            // Title
            val title = when (statType) {
                StatType.STRIKE -> stringResource(Res.string.stat_title_strike)
                StatType.STAR -> stringResource(Res.string.stat_title_star)
                StatType.LEVEL -> stringResource(Res.string.stat_title_coin)
                StatType.HEART -> stringResource(Res.string.stat_title_heart)
            }
            Text(
                text = title.toUpperCase(Locale.current),
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(12.dp))

            // Description
            val description = when (statType) {
                StatType.STRIKE -> stringResource(Res.string.stat_desc_strike)
                StatType.STAR -> stringResource(Res.string.stat_desc_star)
                StatType.LEVEL -> stringResource(Res.string.stat_desc_coin)
                StatType.HEART -> stringResource(Res.string.stat_desc_heart)
            }
            Text(
                text = description,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp)
            )

            if (statType == StatType.HEART && !isFullVersionUnlocked) {
                Spacer(Modifier.height(16.dp))
                ProfileCtaItem(
                    text = stringResource(Res.string.cta_complete),
                    actionLabel = stringResource(Res.string.cta_complete_btn),
                    avatar = {
                        NiloAvatar(
                            userData = userData,
                            face = Faces.Idle,
                            modifier = Modifier.height(56.dp),
                            withBackground = false
                        )
                    },
                    onClick = {
                        if (isWeb) {
                            showFeatureUnavailableDialog = true
                        } else {
                            onPurchaseFullVersion()
                        }
                    },
                    testTagId = "stat_explanation_btn_buy_full"
                )
            }

            if (statType == StatType.LEVEL) {
                val currentXp = userData.experience
                val currentLvl =
                    com.shadowings.gero.domain.model.GameConstants.getPlayerLevel(currentXp)
                val nextLvlXp =
                    com.shadowings.gero.domain.model.GameConstants.getXpRequiredForLevel(currentLvl + 1)
                val prevLvlXp =
                    com.shadowings.gero.domain.model.GameConstants.getXpRequiredForLevel(currentLvl)
                val xpInCurrentLevel = (currentXp - prevLvlXp).coerceAtLeast(0)
                val xpNeededForNextLevel = (nextLvlXp - prevLvlXp).coerceAtLeast(1)
                val progress = xpInCurrentLevel.toFloat() / xpNeededForNextLevel.toFloat()

                Spacer(Modifier.height(16.dp))
                androidx.compose.material3.Text(
                    text = "XP: $xpInCurrentLevel / $xpNeededForNextLevel",
                    style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.SemiBold),
                    color = MaterialTheme.colorScheme.primary
                )
                Spacer(Modifier.height(8.dp))
                LinearProgressIndicator(
                    progress = { progress },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp)
                        .padding(horizontal = 16.dp)
                        .clip(CircleShape),
                    color = MaterialTheme.colorScheme.primary,
                    trackColor = MaterialTheme.colorScheme.primaryContainer
                )
            }

            Spacer(Modifier.height(24.dp))

            if (statType == StatType.STRIKE) {
                val availableStreakRestorations = userData.streakRestorationTokens.values.count { !it }
                if (availableStreakRestorations > 0) {
                    StreakRestorationBanner(
                        availableCount = availableStreakRestorations,
                        deviceInfo = deviceInfo
                    )
                    Spacer(Modifier.height(12.dp))
                }

                com.shadowings.gero.presentation.component.StreakMiniCalendar(
                    completedQuizDates = userData.completedQuizDates,
                    canRestoreStreak = availableStreakRestorations > 0,
                    onRestoreDay = onRedeemStreakRestoration,
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
                Spacer(Modifier.height(16.dp))

                // Not on web: the JS ShareManager can only copy the caption text (no native
                // share sheet), a degraded experience compared to Android/iOS's real share sheet.
                if (!getPlatform().isWeb) {
                    val shareManager = rememberShareManager()
                    val shareTitle = stringResource(Res.string.share_title)
                    val shareText = if (userData.strike == 1) {
                        stringResource(Res.string.share_streak_one, userData.strike)
                    } else {
                        stringResource(Res.string.share_streak_many, userData.strike)
                    }

                    GeroButton(
                        title = stringResource(Res.string.share_btn_text),
                        testTagId = "stat_explanation_btn_share",
                        backgroundColor = MaterialTheme.colorScheme.secondaryContainer,
                        borderColor = MaterialTheme.colorScheme.secondary,
                        textColor = MaterialTheme.colorScheme.onSecondaryContainer,
                        modifier = Modifier.width(300.dp)
                    ) {
                        shareManager(shareText, null, shareTitle)
                    }
                    Spacer(Modifier.height(8.dp))
                }
            }

            // OK Button
            GeroButton(
                title = stringResource(Res.string.ho_capito_btn),
                testTagId = "stat_explanation_btn_close"
            ) {
                onDismiss()
            }
            Spacer(Modifier.height(16.dp))
        }
    }

    if (showFeatureUnavailableDialog) {
        FeatureUnavailableDialog(onDismiss = { showFeatureUnavailableDialog = false })
    }
}

@Composable
private fun StreakRestorationBanner(
    availableCount: Int,
    deviceInfo: com.shadowings.gero.presentation.util.DeviceInfo,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp)
            .clip(androidx.compose.foundation.shape.RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.25f))
            .padding(12.dp)
            .semantics { testTag = "streak_restoration_banner" },
        horizontalArrangement = androidx.compose.foundation.layout.Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = FeatherIcons.RefreshCw,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(28.dp)
        )
        Column {
            Text(
                text = if (availableCount == 1) {
                    stringResource(Res.string.streak_restoration_banner_one)
                } else {
                    stringResource(Res.string.streak_restoration_banner_many, availableCount)
                },
                style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onSurface
            )
            Text(
                text = stringResource(Res.string.streak_restoration_banner_hint),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
